import React, { useMemo, useRef } from 'react';
import { Email } from '../useData';
import { formatDateTime } from '../../../config/appConfig';
import { API_BASE_URL } from '../../../config/apiConfig';
import { Button } from '../../../components/ui';
import { useNotification } from '../../../contexts/NotificationContext';
import { sanitizeEmailBodyForDisplay } from '../utils/sanitizeEmailBody';
import { getNonEmbeddedAttachments } from '../utils/attachments';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';

interface EmailPrintFormProps {
  isOpen: boolean;
  email: Email | null;
  onClose: () => void;
}

const addrEmail = (a: any) => (a && (a.email ?? a.address ?? (typeof a === 'string' ? a : ''))) || '';
const addrName = (a: any) => {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return a.name || addrEmail(a) || '';
};
const formatAddr = (a: any) => {
  const email = addrEmail(a);
  const name = addrName(a);
  if (name && email) return `${name} <${email}>`;
  return name || email || '—';
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build HTML for a minimal print/PDF document (header + body). Same design for print and PDF export.
 */
function buildPrintDocumentHtml(headerHtml: string, bodyHtml: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Print Email</title>
  <style>
    @page { margin: 0.25in; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; font-size: 14px; color: #111; line-height: 1.5; }
    .email-print-header { margin: 0 0 1rem 0; padding: 0; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; }
    .email-print-header-title { margin: 0; padding: 0.5rem 1rem; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; }
    .email-print-header dl { margin: 0; padding: 0.75rem 1rem; }
    .email-print-header .row { margin: 0 0 0.4rem 0; padding: 0; display: block; }
    .email-print-header .row:last-child { margin-bottom: 0; }
    .email-print-header dt { display: inline-block; margin: 0; padding: 0; font-weight: 600; color: #475569; width: 6rem; vertical-align: top; font-size: 13px; }
    .email-print-header dd { display: inline; margin: 0; padding: 0; color: #0f172a; }
    .email-print-body { margin: 0; padding-top: 1rem; border-top: 1px solid #e2e8f0; }
    .email-print-body img { max-width: 100%; height: auto; }
  </style>
</head>
<body>
  <div class="email-print-header">
    <div class="email-print-header-title">Message</div>
    ${headerHtml}
  </div>
  <div class="email-print-body">${bodyHtml}</div>
</body>
</html>`;
}

const EmailPrintForm: React.FC<EmailPrintFormProps> = ({ isOpen, email, onClose }) => {
  const { showError } = useNotification();
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);

  const handlePrint = () => {
    if (!email) return;
    const fromRaw = Array.isArray(email.from) ? email.from[0] : email.from;
    const toList = Array.isArray(email.to) ? email.to : email.to ? [email.to] : [];
    const ccList = Array.isArray(email.cc) ? email.cc : email.cc ? [email.cc] : [];
    const fromDisplay = fromRaw ? formatAddr(fromRaw) : '—';
    const toDisplay = toList.map(formatAddr).join(', ');
    const ccDisplay = ccList.map(formatAddr).join(', ');
    const attachmentList = getNonEmbeddedAttachments(email.attachments);
    const attachmentNames = attachmentList.map((a) => a.filename || '(unnamed)').join(', ');
    const headerHtml = `
      <dl>
        <div class="row"><dt>From:</dt> <dd>${escapeHtml(fromDisplay)}</dd></div>
        <div class="row"><dt>To:</dt> <dd>${escapeHtml(toDisplay || '—')}</dd></div>
        ${ccDisplay ? `<div class="row"><dt>Cc:</dt> <dd>${escapeHtml(ccDisplay)}</dd></div>` : ''}
        <div class="row"><dt>Subject:</dt> <dd>${escapeHtml(email.subject ?? '(no subject)')}</dd></div>
        <div class="row"><dt>Date:</dt> <dd>${escapeHtml(email.date ? formatDateTime(email.date) : '—')}</dd></div>
        ${attachmentNames ? `<div class="row"><dt>Attachments:</dt> <dd>${escapeHtml(attachmentNames)}</dd></div>` : ''}
      </dl>`;
    const bodyHtml = email.bodyHtml && email.id
      ? sanitizeEmailBodyForDisplay(email.bodyHtml, email.id, API_BASE_URL, email.partitionAt)
      : (email.bodyText ? `<pre style="margin:0;white-space:pre-wrap">${escapeHtml(email.bodyText)}</pre>` : '');
    const docHtml = buildPrintDocumentHtml(headerHtml, bodyHtml);

    let iframe = printFrameRef.current;
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'Print');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;overflow:hidden;clip:rect(0,0,0,0);';
      document.body.appendChild(iframe);
      printFrameRef.current = iframe;
    }

    const win = iframe.contentWindow;
    if (!win) {
      showError('Print is not available.');
      return;
    }
    const doc = win.document;
    doc.open();
    doc.write(docHtml);
    doc.close();
    win.focus();
    setTimeout(() => {
      try {
        win.print();
      } catch (e) {
        console.error('Print failed', e);
        showError('Print failed. Please try again.');
      }
    }, 350);
  };

  const sanitizedBodyHtml = useMemo(
    () =>
      email?.bodyHtml && email?.id
        ? sanitizeEmailBodyForDisplay(email.bodyHtml, email.id, API_BASE_URL, email.partitionAt)
        : '',
    [email?.bodyHtml, email?.id, email?.partitionAt]
  );

  if (!isOpen) return null;

  const fromRaw = email && (Array.isArray(email.from) ? email.from[0] : email.from);
  const toList = email ? (Array.isArray(email.to) ? email.to : email.to ? [email.to] : []) : [];
  const ccList = email ? (Array.isArray(email.cc) ? email.cc : email.cc ? [email.cc] : []) : [];
  const fromDisplay = fromRaw ? formatAddr(fromRaw) : '—';
  const toDisplay = toList.map(formatAddr).join(', ');
  const ccDisplay = ccList.map(formatAddr).join(', ');
  const attachmentList = email ? getNonEmbeddedAttachments(email.attachments) : [];
  const attachmentNamesDisplay = attachmentList.map((a) => a.filename || '(unnamed)').join(', ');

  return (
    <div
      className="email-print-form-root fixed inset-0 flex items-start justify-center overflow-auto bg-black/50 p-4 print:block print:bg-white print:p-0"
      style={zStyle(Z_INDEX.modalHigh)}
      role="dialog"
      aria-modal="true"
      aria-label="Print email"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="email-print-form-panel w-full max-w-4xl rounded-lg bg-white shadow-xl print:max-w-none print:shadow-none print:rounded-none" onClick={(e) => e.stopPropagation()}>
        <div className="email-print-form-actions flex items-center justify-end gap-2 border-b border-gray-200 px-6 py-4 print:hidden">
          <Button
            variant="ghost"
            onClick={handlePrint}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            title="Print"
            aria-label="Print"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            title="Close"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
        <div className="email-print-form-content p-6 print:p-0">
          {/* Header: From, To, Cc, Subject, Date */}
          <dl className="email-print-header-dl text-sm text-gray-900 space-y-1.5 mb-6 print:mb-4">
            <div><dt className="inline font-semibold w-14">From:</dt> <dd className="inline">{fromDisplay}</dd></div>
            <div><dt className="inline font-semibold w-14">To:</dt> <dd className="inline">{toDisplay || '—'}</dd></div>
            {ccDisplay ? <div><dt className="inline font-semibold w-14">Cc:</dt> <dd className="inline">{ccDisplay}</dd></div> : null}
            <div><dt className="inline font-semibold w-14">Subject:</dt> <dd className="inline">{email?.subject ?? '(no subject)'}</dd></div>
            <div><dt className="inline font-semibold w-14">Date:</dt> <dd className="inline">{email?.date ? formatDateTime(email.date) : '—'}</dd></div>
            {attachmentNamesDisplay ? <div><dt className="inline font-semibold w-14">Attachments:</dt> <dd className="inline">{attachmentNamesDisplay}</dd></div> : null}
          </dl>
          {/* Message body: div (not iframe) so it flows across pages when printing */}
          <div className="email-print-form-body border-t border-gray-200 pt-4 print:border-t print:pt-4 max-h-[60vh] overflow-y-auto print:max-h-none print:overflow-visible">
            {email?.bodyHtml ? (
              <div
                className="email-print-form-body-inner text-gray-900 text-sm leading-relaxed [&_img]:max-w-full [&_img]:h-auto"
                dangerouslySetInnerHTML={{ __html: sanitizedBodyHtml }}
              />
            ) : (
              <pre className="whitespace-pre-wrap text-gray-900 font-sans text-sm leading-relaxed">
                {email?.bodyText ?? '(no content)'}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPrintForm;
